import {
  isEmail,
  isMandatory,
  isInRange,
  isMobilePhone,
  foreach,
  isChassisNumber,
  isValidDateFormat,
  isValidRocketUIDateFormat
} from 'utils/simple-validations';
import { round } from '../assetFunctions';
import { when, both, valueChanged, isNumber, nullWhen, prop, not } from 'utils/fn-combinators';
import * as numericLimits from 'constants/numericLimits';
import { view_C187F9E5_83C2_4DBD_ADA7_B17523A862C7 } from 'constants/viewType';
import { getDynamicFieldControlType } from 'utils/functions';
import { ControlType } from '@totalsoft/rocket-ui';
import { supplierType } from '../constants';

export const getBusinessRules = (vatPercent, _isNewAsset = false) => {
  return {
    acquisitionPriceWithoutVat: when(
      both(
        valueChanged(asset => asset.acquisitionPriceWithVat),
        isNumber(asset => asset.acquisitionPriceWithVat)
      ),
      asset => round(2)(asset.acquisitionPriceWithVat / (1 + vatPercent / 100))
    ),
    acquisitionPriceWithVat: when(
      both(
        both(
          valueChanged(asset => asset.acquisitionPriceWithoutVat),
          not(valueChanged(asset => asset.acquisitionPriceWithVat))
        ),
        isNumber(asset => asset.acquisitionPriceWithoutVat)
      ),
      asset => round(2)(asset.acquisitionPriceWithoutVat * (1 + vatPercent / 100))
    ),
    hierarchy: when(
      valueChanged(asset => asset.assetTypeId),
      () => resetHierarchyLevels()
    ),
    chassisNumber: nullWhen(valueChanged(asset => asset.isChassisNumberAvailable)),
    partner: {
      supplier: nullWhen(valueChanged(asset => asset.partner.supplierType === supplierType.Other )),
      dealer: {
        name: nullWhen(valueChanged(asset => asset.partner.dealer?.registrationCode === '')),
        dealerId: nullWhen(valueChanged(asset => asset.partner.dealer?.registrationCode === ''))
      }
    }
  };
};

const resetHierarchyLevels = () => {
  return Object.assign([], { loaded: false });
};

const getValidationRulesDefault = (
  hasRightToEditFinancedAssetDealer,
  isFacilityFrame,
  chassisNumberIsDirty = false,
  popupChassisNumberValidation = false,
  isSecondHand = false,
  isChassisNumberAvailable = false,
  hasWorkingHours = false,
  supplierType
) => {
  let validationRules = {
    assetTypeId: [isMandatory()],
    quantity: [isMandatory(), isInRange(numericLimits.minimumNaturalNumber + 1, numericLimits.maximumQuantity)],
    acquisitionPriceWithoutVat: [isMandatory(), isInRange(numericLimits.minimumAmount, numericLimits.maximumAmountWithoutVat)],
    acquisitionPriceWithVat: [isMandatory(), isInRange(numericLimits.minimumAmount, numericLimits.maximumAmountWithoutVat)],
    buildYearAndMonth: [when(isSecondHand, isMandatory()), isValidDateFormat()],
    km: [when(isSecondHand && !hasWorkingHours, isInRange(numericLimits.minimumNaturalNumber + 1, numericLimits.maximumOdometer))],
    workingHours: [when(isSecondHand && hasWorkingHours, isInRange(numericLimits.minimumNaturalNumber + 1, numericLimits.maximumOdometer))],
    chassisNumber: [
      when(isChassisNumberAvailable, isMandatory()),
      when(isChassisNumberAvailable && chassisNumberIsDirty && !popupChassisNumberValidation, isMandatory()),
      when(isChassisNumberAvailable && chassisNumberIsDirty, isChassisNumber())
    ],
    partner: {
      dealer: {
        contactInfo: {
          name: [when(hasRightToEditFinancedAssetDealer && prop('partner.dealer.contactInfo.name'), isMandatory())],
          email: [when(hasRightToEditFinancedAssetDealer && prop('partner.dealer.contactInfo.name'), isEmail())],
          phone: [when(hasRightToEditFinancedAssetDealer && prop('partner.dealer.contactInfo.name'), isMobilePhone())]
        },
        dealerId: [
          when(hasRightToEditFinancedAssetDealer && (isFacilityFrame ? prop('partner.dealer.registrationCode') : true), isMandatory())
        ]
      },
      supplier: {
        contactInfo: {
          email: [when(hasRightToEditFinancedAssetDealer && supplierType === supplierType.Other && not(prop('partner.supplier.contactInfo')), isEmail())],
          phone: [
            when(hasRightToEditFinancedAssetDealer && supplierType === supplierType.Other && not(prop('partner.supplier.contactInfo')), isMobilePhone())
          ]
        },
        registrationCode: [when(hasRightToEditFinancedAssetDealer && supplierType === supplierType.Other, isMandatory())],
        supplierId: [when(hasRightToEditFinancedAssetDealer && supplierType === supplierType.Other, isMandatory())]
      }
    },
    properties:
      !isFacilityFrame &&
      when(
        prop('properties'),
        foreach(item => ({
          value: [
            item.mandatory && isMandatory(),
            when(getDynamicFieldControlType(item.typeCode) === ControlType.Date, isValidRocketUIDateFormat())
          ]
        }))
      )
  };

  return validationRules;
};

const getValidationRulesAlpha = (
  financedAssetDealerWrite,
  isFacilityFrame,
  chassisNumberIsDirty = false,
  popupChassisNumberValidation = false,
  isSecondHand = false,
  isChassisNumberAvailable = false,
  hasWorkingHours = false,
  supplierType
) => {
  let validationRules = {
    assetTypeId: [isMandatory()],
    quantity: [isMandatory(), isInRange(numericLimits.minimumNaturalNumber + 1, numericLimits.maximumQuantity)],
    acquisitionPriceWithoutVat: [isMandatory(), isInRange(numericLimits.minimumAmount, numericLimits.maximumAmountWithoutVat)],
    category: [isMandatory()],
    acquisitionPriceWithVat: [isMandatory(), isInRange(numericLimits.minimumAmount, numericLimits.maximumAmountWithoutVat)],
    buildYearAndMonth: [when(isSecondHand, isMandatory()), isValidDateFormat()],
    km: [
      when(
        isSecondHand && !isFacilityFrame && !hasWorkingHours,
        isInRange(numericLimits.minimumNaturalNumber + 1, numericLimits.maximumOdometer)
      )
    ],
    chassisNumber: [
      when(isChassisNumberAvailable, isMandatory()),
      when(isChassisNumberAvailable && chassisNumberIsDirty && !popupChassisNumberValidation, isMandatory()),
      when(isChassisNumberAvailable && chassisNumberIsDirty, isChassisNumber())
    ],
    partner: {
      dealer: {
        contactInfo: {
          name: [when(financedAssetDealerWrite && prop('partner.dealer.contactInfo.name'), isMandatory())],
          email: [when(financedAssetDealerWrite && prop('partner.dealer.contactInfo.name'), isEmail())],
          phone: [when(financedAssetDealerWrite && prop('partner.dealer.contactInfo.name'), isMobilePhone())]
        },
        dealerId: [when(financedAssetDealerWrite && (isFacilityFrame ? prop('partner.dealer.registrationCode') : true), isMandatory())]
      },
      supplier: {
        contactInfo: {
          email: [when(financedAssetDealerWrite && supplierType === supplierType.Other && not(prop('partner.supplier.contactInfo')), isEmail())],
          phone: [when(financedAssetDealerWrite && supplierType === supplierType.Other && not(prop('partner.supplier.contactInfo')), isMobilePhone())]
        },
        registrationCode: [when(financedAssetDealerWrite && supplierType === supplierType.Other, isMandatory())],
        supplierId: [when(financedAssetDealerWrite && supplierType === supplierType.Other, isMandatory())]
      }
    },
    properties:
      !isFacilityFrame &&
      when(
        prop('properties'),
        foreach(item => ({
          value: [
            item.mandatory && isMandatory(),
            when(getDynamicFieldControlType(item.typeCode) === ControlType.Date, isValidRocketUIDateFormat())
          ]
        }))
      )
  };

  return validationRules;
};

const customRules = {
  [view_C187F9E5_83C2_4DBD_ADA7_B17523A862C7]: getValidationRulesAlpha
};

export const getValidationRules = viewType => customRules[viewType] || getValidationRulesDefault;
